'use client';

import React, { useState, useRef, useEffect } from 'react';
import { SearchInput } from '@/components/ui/search-input';
import { cn } from '@/lib/utils';
import { Checkbox } from '../ui/checkbox';

type SidebarProps = {
  className?: string;
  children?: React.ReactNode;
};

type SidebarHeaderProps = {
  title?: React.ReactNode;
  subtitle?: string;
  onSearchChange?: (query: string) => void;
  hideSearch?: boolean;
};

type SidebarContentProps = {
  children?: React.ReactNode;
  className?: string;
  title?: string;
};

type FilterGroupProps = {
  title: string;
  children: React.ReactNode;
  className?: string;
};

type FilterItemProps = {
  label: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  testId?: string;
};

type MenuSectionProps = {
  title?: string;
  children: React.ReactNode;
  className?: string;
};

type MenuItemProps = {
  label: string;
  onClick?: () => void;
  href?: string;
  active?: boolean;
  className?: string;
  testId?: string;
};

export const SidebarHeader = ({
  title = 'Free Online Practice',
  onSearchChange,
  hideSearch = false,
}: SidebarHeaderProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const isMobileDevice =
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    const isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobile = isMobileDevice || isTouchDevice;

    if (isMobile && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.blur();
      }, 0);
    }
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    onSearchChange?.(value);
  };

  return (
    <div className='mb-6 flex flex-col gap-2'>
      <h2 className='text-base'>
        {title}
      </h2>

      {!hideSearch && (
        <SearchInput
          ref={inputRef}
          className='h-11 text-base'
          value={searchQuery}
          onChange={handleSearchChange}
          testId='sidebar_search_input'
        />
      )}
    </div>
  );
};

export const SidebarContent = ({
  children,
  className,
  title,
}: SidebarContentProps) => {
  return (
    <div
      className={cn(
        'flex-1 overflow-auto pb-4 md:pb-6',
        className
      )}
    >
      {title && <h3 className='pb-3.5 text-2xl font-semibold'>{title}</h3>}
      {children}
    </div>
  );
};

export const FilterGroup = ({
  title,
  children,
  className,
}: FilterGroupProps) => {
  return (
    <div className={cn('px-1 py-1.5', className)}>
      <h3 className='my-1.5 text-base font-semibold'>{title}</h3>
      <div className='space-y-2'>{children}</div>
    </div>
  );
};

export const FilterItem = ({
  label,
  checked = false,
  onChange,
  testId,
}: FilterItemProps) => {
  return (
    <label
      className='mb-0 py-2 px-1.5 flex items-center gap-3 cursor-pointer group'
      data-testid={testId}
    >
      <Checkbox checked={checked} onCheckedChange={onChange} />
      <span className='text-sm font-medium select-none'>
        {label}
      </span>
    </label>
  );
};

export const MenuSection = ({
  title,
  children,
  className,
}: MenuSectionProps) => {
  return (
    <div className={cn('mb-6', className)}>
      {title && (
        <h3 className='text-base md:text-lg font-bold mb-3 text-zinc-900'>
          {title}
        </h3>
      )}
      <nav className='space-y-1'>{children}</nav>
    </div>
  );
};

export const MenuItem = ({
  label,
  onClick,
  href,
  active = false,
  className,
  testId,
}: MenuItemProps) => {
  const baseClasses = cn(
    'block px-3 py-2 text-base rounded-md transition-colors hover:text-zinc-500 cursor-pointer',
    active
      ? 'text-accent-blue font-semibold'
      : 'text-black hover:bg-zinc-100',
    className
  );

  if (href) {
    return (
      <a href={href} className={baseClasses} data-testid={testId}>
        {label}
      </a>
    );
  }

  return (
    <button
      onClick={onClick}
      className={cn(baseClasses, 'w-full text-left')}
      data-testid={testId}
    >
      {label}
    </button>
  );
};

const Sidebar = ({ className, children }: SidebarProps) => {
  return (
    <div
      className={cn(
        'flex flex-col h-full bg-zinc-100',
        className
      )}
    >
      {children}
    </div>
  );
};

Sidebar.Header = SidebarHeader;
Sidebar.Content = SidebarContent;
Sidebar.FilterGroup = FilterGroup;
Sidebar.FilterItem = FilterItem;
Sidebar.MenuSection = MenuSection;
Sidebar.MenuItem = MenuItem;

export default Sidebar;
